import type { ServiceAvailabilityResponse } from '../../../core/models/service-availability';

/** Kilograms-per-pound factor aligned with Product Master / Express Pack conversion. */
const KILOGRAMS_PER_POUND = 0.45359237;

/** PSA product fields used for agent weight limit clamping (OM-872). */
export interface PsaAgentWeightProduct {
  productNumber: number | string;
  pickupAgentWeightLimit?: number | string;
  pickupAgentWeightUnit?: string;
  deliveryAgentWeightLimit?: number | string;
  deliveryAgentWeightUnit?: string;
}

/**
 * Flattens available PSA products from a service-availability response for agent weight clamping.
 */
export function extractPsaAgentWeightProducts(
  response: ServiceAvailabilityResponse | null | undefined,
): PsaAgentWeightProduct[] {
  const pairs = response?.senderReceiverPairs ?? [];
  const products: PsaAgentWeightProduct[] = [];

  for (const pair of pairs) {
    for (const product of pair.availableProducts ?? []) {
      if (product == null) {
        continue;
      }
      products.push({
        productNumber: product.productNumber,
        pickupAgentWeightLimit: product.pickupAgentWeightLimit,
        pickupAgentWeightUnit: product.pickupAgentWeightUnit,
        deliveryAgentWeightLimit: product.deliveryAgentWeightLimit,
        deliveryAgentWeightUnit: product.deliveryAgentWeightUnit,
      });
    }
  }

  return products;
}

/**
 * Parses a single PSA agent weight limit into pounds when the value is usable.
 * Missing, non-numeric, or non-positive limits are ignored (no clamp).
 */
function readAgentLimitLb(limitValue: unknown, unitValue: unknown): number | undefined {
  const limit =
    typeof limitValue === 'number'
      ? limitValue
      : typeof limitValue === 'string'
        ? Number.parseFloat(limitValue.trim())
        : Number.NaN;

  if (!Number.isFinite(limit) || limit <= 0) {
    return undefined;
  }

  const unit =
    typeof unitValue === 'string' && unitValue.trim().length > 0
      ? unitValue.trim().toUpperCase()
      : 'LB';

  if (unit === 'KG' || unit === 'KGS') {
    const converted = limit / KILOGRAMS_PER_POUND;
    return Number.isFinite(converted) && converted > 0 ? converted : undefined;
  }

  if (unit === 'LB' || unit === 'LBS') {
    return limit;
  }

  return undefined;
}

/**
 * Normalizes a PSA or catalog product code for intersection matching.
 */
function normalizeProductCode(value: number | string): string {
  return String(value).trim().toLowerCase();
}

/**
 * Resolves the per-product agent weight max in lb from pickup/delivery PSA fields.
 * Uses the minimum of pickup and delivery when both are valid; one present → that value.
 */
export function resolveAgentMaxWeightLbForProduct(
  product: PsaAgentWeightProduct,
): number | undefined {
  const pickup = readAgentLimitLb(product.pickupAgentWeightLimit, product.pickupAgentWeightUnit);
  const delivery = readAgentLimitLb(
    product.deliveryAgentWeightLimit,
    product.deliveryAgentWeightUnit,
  );

  const candidates = [pickup, delivery].filter(
    (value): value is number => typeof value === 'number',
  );
  if (candidates.length === 0) {
    return undefined;
  }

  return Math.min(...candidates);
}

/**
 * Resolves the effective package weight max in lb for UI validators.
 * Takes min(catalog, agent) across PSA products intersecting the package `productCodes`.
 * No intersection (or unusable agent limits) → catalog max only; never raises catalog.
 *
 * @param catalogMaxLb - Product Master `constraints.weight.max` in pounds
 * @param packageProductCodes - Product codes for the selected package type
 * @param psaProducts - Available products from the Package-step PSA response
 */
export function resolveEffectivePackageWeightMaxLb(
  catalogMaxLb: number | undefined | null,
  packageProductCodes: readonly string[],
  psaProducts: readonly PsaAgentWeightProduct[],
): number | undefined {
  if (typeof catalogMaxLb !== 'number' || !Number.isFinite(catalogMaxLb) || catalogMaxLb <= 0) {
    return undefined;
  }

  const packageCodeSet = new Set(
    packageProductCodes.map(normalizeProductCode).filter((code) => code.length > 0),
  );

  const intersectingAgentMaxes: number[] = [];
  for (const product of psaProducts) {
    const code = normalizeProductCode(product.productNumber);
    if (!packageCodeSet.has(code)) {
      continue;
    }
    const agentMax = resolveAgentMaxWeightLbForProduct(product);
    if (typeof agentMax === 'number') {
      intersectingAgentMaxes.push(agentMax);
    }
  }

  if (intersectingAgentMaxes.length === 0) {
    return catalogMaxLb;
  }

  return Math.min(catalogMaxLb, ...intersectingAgentMaxes);
}
